import React from 'react';
import { FieldArray, Form, Formik, getIn, useFormikContext } from 'formik';
import * as yup from 'yup';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Add, Cancel, Delete, Done } from '@material-ui/icons';
import { useCreateOrUpdateArticle } from '../../hooks';
import { Confluence } from '../../interfaces';

const useStyles = makeStyles({
  table: {
    '& thead': {
      '& th': {
        fontWeight: 'bold',
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          backgroundColor: '#FAFAFA',
        },
      },
    },
  },
});

const POLL_QUESTION = {
  questionKk: '',
  questionRu: '',
};

const initialValues: Confluence.IPollArticle = {
  name: '',
  template: {
    descriptionKk: '',
    descriptionRu: '',
    subjectKk: '',
    subjectRu: '',
    questions: [POLL_QUESTION],
  },
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Обязательное поле'),
  template: yup.object().shape({
    subjectKk: yup.string().required('Обязательное поле'),
    subjectRu: yup.string().required('Обязательное поле'),
    descriptionKk: yup.string().required('Обязательное поле'),
    descriptionRu: yup.string().required('Обязательное поле'),
    questions: yup
      .array()
      .of(
        yup.object().shape({
          questionKk: yup.string().required('Обязательное поле'),
          questionRu: yup.string().required('Обязательное поле'),
        }),
      )
      .min(1, 'Должен быть хотя бы один вопрос'),
  }),
});

interface IProps {
  article?: Confluence.IPollArticle | null;
  parentId?: number | null;
  onClose(): void;
  onCreateOrUpdate?(article: Confluence.IArticleBase): void;
}

const PollTemplate = ({ article, parentId, onClose, onCreateOrUpdate }: IProps) => {
  const { loading, handleCreateOrUpdatePollArticle } = useCreateOrUpdateArticle(article, parentId, (articleData) => {
    onClose();
    if (onCreateOrUpdate) onCreateOrUpdate(articleData);
  });

  // Main render
  return (
    <Formik
      onSubmit={handleCreateOrUpdatePollArticle}
      validationSchema={validationSchema}
      initialValues={article ? article : initialValues}
      enableReinitialize
    >
      <Form>
        <DialogContent dividers style={{ overflow: 'visible' }}>
          <PollTemplateForm article={article} loading={loading} />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
            Отменить
          </Button>

          <Button color="primary" variant="contained" disabled={loading} type="submit" startIcon={<Done />}>
            {article ? 'Обновить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Form>
    </Formik>
  );
};

interface IPollTemplateForm {
  article?: Confluence.IPollArticle | null;
  loading: boolean;
}

const PollTemplateForm = ({ loading }: IPollTemplateForm) => {
  const { values, touched, errors, handleChange } = useFormikContext<Confluence.IPollArticle>();

  // Main render
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="name"
          value={values.name}
          variant="filled"
          label="Заголовок статьи"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.subjectKk"
          value={values.template.subjectKk}
          variant="filled"
          label="Заголовок опроса (Казахский)"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.subjectKk && errors.template?.subjectKk)}
          helperText={touched.template?.subjectKk && errors.template?.subjectKk}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.subjectRu"
          value={values.template.subjectRu}
          variant="filled"
          label="Заголовок опроса (Русский)"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.subjectRu && errors.template?.subjectRu)}
          helperText={touched.template?.subjectRu && errors.template?.subjectRu}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          multiline
          minRows={4}
          name="template.descriptionKk"
          value={values.template.descriptionKk}
          variant="filled"
          label="Описание (Казахский)"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.descriptionKk && errors.template?.descriptionKk)}
          helperText={touched.template?.descriptionKk && errors.template?.descriptionKk}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          multiline
          minRows={4}
          name="template.descriptionRu"
          value={values.template.descriptionRu}
          variant="filled"
          label="Описание (Русский)"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.descriptionRu && errors.template?.descriptionRu)}
          helperText={touched.template?.descriptionRu && errors.template?.descriptionRu}
        />
      </Grid>

      <Grid item xs={12}>
        <PollQuestions loading={loading} />
      </Grid>
    </Grid>
  );
};

const PollQuestions = ({ loading }: Pick<IPollTemplateForm, 'loading'>) => {
  const { values, errors, touched, handleChange } = useFormikContext<Confluence.IPollArticle>();

  const classes = useStyles();

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Вопрос (Казахский)</TableCell>
            <TableCell>Вопрос (Русский)</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <FieldArray
          name="template.questions"
          render={({ push, remove, replace }) => {
            return (
              <>
                <TableBody>
                  {values.template.questions.map((question, index) => {
                    const field = (name: string) => `template.questions[${index}].${name}`;

                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            name={field('questionKk')}
                            value={question.questionKk}
                            variant="filled"
                            placeholder="Введите значение"
                            disabled={loading}
                            fullWidth
                            onChange={handleChange}
                            error={Boolean(getIn(touched, field('questionKk')) && getIn(errors, field('questionKk')))}
                            size="small"
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name={field('questionRu')}
                            value={question.questionRu}
                            variant="filled"
                            placeholder="Введите значение"
                            disabled={loading}
                            fullWidth
                            onChange={handleChange}
                            error={Boolean(getIn(touched, field('questionRu')) && getIn(errors, field('questionRu')))}
                            size="small"
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Удалить вопрос">
                            <IconButton
                              aria-label="Удалить вопрос"
                              size="small"
                              onClick={() =>
                                values.template.questions.length !== 1 ? remove(index) : replace(0, POLL_QUESTION)
                              }
                              disabled={loading}
                            >
                              <Delete color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Tooltip title="Добавить поля">
                        <IconButton
                          color="primary"
                          disabled={loading}
                          aria-label="Добавить поля"
                          onClick={() => push(POLL_QUESTION)}
                        >
                          <Add />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </>
            );
          }}
        />
      </Table>
    </TableContainer>
  );
};

export default PollTemplate;
